import type { FC, RefAttributes } from "react";
import type {
	ButtonProps as AriaButtonProps,
	MenuItemProps as AriaMenuItemProps,
	MenuProps as AriaMenuProps,
	PopoverProps as AriaPopoverProps,
	SeparatorProps as AriaSeparatorProps,
} from "react-aria-components";

import { DotsVertical } from "@untitledui/icons";
import {
	Button as AriaButton,
	Menu as AriaMenu,
	MenuItem as AriaMenuItem,
	Popover as AriaPopover,
	Separator as AriaSeparator,
} from "react-aria-components";

import { cx } from "@/utils/cx";

interface DropdownItemProps extends AriaMenuItemProps {
	/** An addon to be displayed on the right side of the item. */
	addon?: string;
	/** Secondary line under the label (e.g. why a disabled item is unavailable). */
	description?: string;
	/** An icon to be displayed on the left side of the item. */
	icon?: FC<{ className?: string }>;
	/** The label of the item to be displayed. */
	label?: string;
	/**
	 * Native HTML tooltip. Defaults to `description` when the item is disabled.
	 * Applied on the inner content — `MenuItem` does not accept `title`.
	 */
	title?: string;
	/** If true, the item will not have any styles. */
	unstyled?: boolean;
}

export const DropdownItem = ({
	addon,
	children,
	description,
	icon: Icon,
	label,
	title,
	unstyled,
	...props
}: DropdownItemProps) => {
	if (unstyled) {
		return <AriaMenuItem id={label} textValue={label} {...props} />;
	}

	const tooltipTitle = title ?? (props.isDisabled ? description : undefined);

	return (
		<AriaMenuItem
			{...props}
			aria-label={
				props["aria-label"] ?? (props.isDisabled ? description : undefined)
			}
			className={(state) =>
				cx(
					"group block cursor-pointer px-1.5 py-px outline-hidden",
					state.isDisabled && "cursor-not-allowed opacity-50",
					typeof props.className === "function"
						? props.className(state)
						: props.className,
				)
			}
			textValue={props.textValue ?? label}
		>
			{(state) => (
				<div
					className={cx(
						"relative flex items-center rounded-md px-2.5 py-2 outline-focus-ring transition duration-100 ease-linear",
						!state.isDisabled && "group-hover:bg-primary_hover",
						state.isFocused && "bg-primary_hover",
						state.isFocusVisible && "outline-2 -outline-offset-2",
					)}
					title={tooltipTitle}
				>
					{Icon && (
						<Icon
							aria-hidden="true"
							className={cx(
								"mr-2 size-4 shrink-0 stroke-[2.25px] text-fg-quaternary",
							)}
						/>
					)}

					<span className="min-w-0 grow">
						<span
							className={cx(
								"block truncate text-sm font-semibold text-secondary",
								state.isFocused && "text-secondary_hover",
							)}
						>
							{label ||
								(typeof children === "function" ? children(state) : children)}
						</span>
						{description ? (
							<span className="mt-0.5 block text-xs font-normal text-tertiary">
								{description}
							</span>
						) : null}
					</span>

					{addon && (
						<span
							className={cx(
								"ml-3 shrink-0 rounded px-1 py-px text-xs font-medium text-quaternary ring-1 ring-secondary ring-inset",
							)}
						>
							{addon}
						</span>
					)}
				</div>
			)}
		</AriaMenuItem>
	);
};

type DropdownMenuProps<T extends object> = AriaMenuProps<T>;

export const DropdownMenu = <T extends object>(props: DropdownMenuProps<T>) => {
	return (
		<AriaMenu
			disallowEmptySelection
			selectionMode="single"
			{...props}
			className={(state) =>
				cx(
					"h-min overflow-y-auto py-1 outline-hidden select-none",
					typeof props.className === "function"
						? props.className(state)
						: props.className,
				)
			}
		/>
	);
};

type DropdownPopoverProps = AriaPopoverProps & {
	footer?: React.ReactNode;
	header?: React.ReactNode;
};

export const DropdownPopover = (props: DropdownPopoverProps) => {
	const { children, footer, header } = props;
	return (
		<AriaPopover
			placement="bottom right"
			{...props}
			className={(state) =>
				cx(
					"w-62 origin-(--trigger-anchor-point) overflow-auto rounded-lg bg-primary shadow-lg ring-1 ring-secondary_alt will-change-transform",
					state.isEntering &&
						"duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5",
					state.isExiting &&
						"duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5",
					typeof props.className === "function"
						? props.className(state)
						: props.className,
				)
			}
		>
			<>
				{header && (
					<div className="border-b border-border-secondary p-3 text-sm font-medium text-secondary">
						{header}
					</div>
				)}
				{children}
				{footer && (
					<div className="border-t border-border-secondary p-3 text-sm font-medium text-secondary">
						{footer}
					</div>
				)}
			</>
		</AriaPopover>
	);
};

export const DropdownSeparator = (props: AriaSeparatorProps) => {
	return (
		<AriaSeparator
			{...props}
			className={cx("my-1 h-px w-full bg-border-secondary", props.className)}
		/>
	);
};

export const DropdownDotsButton = (
	props: AriaButtonProps & RefAttributes<HTMLButtonElement>,
) => {
	return (
		<AriaButton
			{...props}
			aria-label="Open menu"
			className={(state) =>
				cx(
					"cursor-pointer rounded-md text-fg-quaternary outline-focus-ring transition duration-100 ease-linear",
					(state.isPressed || state.isHovered) && "text-fg-quaternary_hover",
					(state.isPressed || state.isFocusVisible) &&
						"outline-2 outline-offset-2",
					typeof props.className === "function"
						? props.className(state)
						: props.className,
				)
			}
		>
			<DotsVertical className="size-5 transition-inherit-all" />
		</AriaButton>
	);
};
